import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import type { EditableQuoteItem, EditableQuoteItemPatch } from "@/lib/quotations/editor-form";
import { majorToMinor, minorToMajor, type CurrencyCode } from "@/lib/currency/currency";
import type { QuoteColumn } from "@/lib/quotations/columns";
import type { ItemStatus } from "@/lib/pricing/types";
import { cn } from "@/lib/utils/cn";

type QuoteSection = {
  id: string;
  titleEn: string;
  titleZh: string;
};

type QuoteTableItem = EditableQuoteItem & {
  sectionId: string;
  sortOrder?: number;
};

type ItemsTableProps = {
  sections: QuoteSection[];
  items: QuoteTableItem[];
  onItemChange: (itemId: string, patch: EditableQuoteItemPatch) => void;
  onAddItem: (sectionId: string) => void;
  onMoveSection: (sectionId: string, direction: "up" | "down") => void;
  onDeleteSection: (sectionId: string) => void;
  onMoveItem: (itemId: string, direction: "up" | "down") => void;
  onDuplicateItem: (itemId: string) => void;
  onDeleteItem: (itemId: string) => void;
  mutating?: string | null;
  columns?: QuoteColumn[];
  currencyCode?: CurrencyCode;
};

const itemStatuses: ItemStatus[] = ["normal", "included", "in_package", "optional", "tbc", "actual_cost", "hidden"];

export function ItemsTable({
  sections,
  items,
  onItemChange,
  onAddItem,
  onMoveSection,
  onDeleteSection,
  onMoveItem,
  onDuplicateItem,
  onDeleteItem,
  mutating = null,
  columns = [],
  currencyCode = "RMB",
}: ItemsTableProps) {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const sectionNames = new Map(sections.map((section) => [section.id, language === "zh" ? section.titleZh : section.titleEn]));
  const customColumns = columns.filter((column) => column.type === "custom" && column.visible);
  const customGrid = customColumns.length > 0 ? ` repeat(${customColumns.length}, 160px)` : "";
  const gridTemplateColumns = `180px 240px 240px 120px 84px 84px 130px 130px 150px 90px 80px 105px${customGrid} 150px`;
  const controlsBusy = mutating !== null;

  return (
    <div className="min-w-[1890px]">
      <div className="grid items-center gap-2 border-b border-border bg-muted px-3 py-2 text-xs font-medium text-muted-foreground" style={{ gridTemplateColumns }}>
        <span className="sticky left-0 z-10 bg-muted">{text.module}</span>
        <span>{text.itemEn}</span>
        <span>{text.itemZh}</span>
        <span>{text.unit}</span>
        <span>{text.days}</span>
        <span>{text.qty}</span>
        <span>{text.rate}</span>
        <span>{text.amount}</span>
        <span>{text.status}</span>
        <span>{text.client}</span>
        <span>{text.tax}</span>
        <span>{text.total}</span>
        {customColumns.map((column) => (
          <span key={column.key}>{language === "zh" ? column.labelZh : column.labelEn}</span>
        ))}
        <span className="sticky right-0 z-10 bg-muted text-center">{text.actions}</span>
      </div>
      {sections.map((section, sectionIndex) => {
        const sectionItems = items
          .filter((item) => item.sectionId === section.id)
          .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
        const sectionName = sectionNames.get(section.id) ?? section.id;

        return (
          <div key={section.id}>
            <div className="grid items-center gap-2 border-b border-border bg-muted/60 px-3 py-2" style={{ gridTemplateColumns }}>
              <div className="truncate text-xs font-semibold text-foreground" style={{ gridColumn: "1 / -2" }}>{sectionName}</div>
              <div className="sticky right-0 z-10 flex items-center justify-center gap-1 bg-muted/60 pl-2" style={{ gridColumn: "-2 / -1" }}>
                <ActionButton title={`${text.addItem}: ${sectionName}`} disabled={controlsBusy} onClick={() => onAddItem(section.id)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </ActionButton>
                <ActionButton title={`${text.moveUp}: ${sectionName}`} disabled={controlsBusy || sectionIndex === 0} onClick={() => onMoveSection(section.id, "up")}>
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </ActionButton>
                <ActionButton title={`${text.moveDown}: ${sectionName}`} disabled={controlsBusy || sectionIndex === sections.length - 1} onClick={() => onMoveSection(section.id, "down")}>
                  <ArrowDown className="h-4 w-4" aria-hidden="true" />
                </ActionButton>
                <ActionButton title={`${text.deleteModule}: ${sectionName}`} disabled={controlsBusy} destructive onClick={() => onDeleteSection(section.id)}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </ActionButton>
              </div>
            </div>
            {sectionItems.map((item, itemIndex) => (
              <div
                key={item.id}
                className="grid items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0"
                style={{ gridTemplateColumns }}
              >
                <span className="sticky left-0 z-10 truncate bg-background py-1.5 pr-2 text-muted-foreground">{sectionNames.get(item.sectionId) ?? item.sectionId}</span>
                <CellInput value={item.nameEn} onChange={(value) => onItemChange(item.id, { nameEn: value })} />
                <CellInput value={item.nameZh ?? ""} onChange={(value) => onItemChange(item.id, { nameZh: value })} />
                <CellInput value={item.unit} onChange={(value) => onItemChange(item.id, { unit: value })} />
                <CellInput type="number" value={item.days ?? ""} onChange={(value) => onItemChange(item.id, { days: value === "" ? null : Number(value) })} />
                <CellInput type="number" value={item.quantity} onChange={(value) => onItemChange(item.id, { quantity: Number(value) })} />
                <CellInput type="number" step={currencyCode === "JPY" ? "1" : "0.01"} value={minorToMajor(item.rateMinor, currencyCode)} onChange={(value) => onItemChange(item.id, { rateMinor: Math.max(majorToMinor(value, currencyCode), 0) })} />
                <CellInput type="number" step={currencyCode === "JPY" ? "1" : "0.01"} value={minorToMajor(item.amountMinor, currencyCode)} onChange={(value) => onItemChange(item.id, { amountMinor: Math.max(majorToMinor(value, currencyCode), 0) })} />
                <select
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  value={item.status}
                  onChange={(event) => onItemChange(item.id, { status: event.target.value as ItemStatus })}
                >
                  {itemStatuses.map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status, language)}
                    </option>
                  ))}
                </select>
                <CheckCell checked={item.visibleToClient === true || item.visibleToClient === 1} onChange={(checked) => onItemChange(item.id, { visibleToClient: checked })} />
                <CheckCell checked={item.taxable === true || item.taxable === 1} onChange={(checked) => onItemChange(item.id, { taxable: checked })} />
                <CheckCell checked={item.includeInTotal === true || item.includeInTotal === 1} onChange={(checked) => onItemChange(item.id, { includeInTotal: checked })} />
                {customColumns.map((column) => (
                  <CellInput
                    key={column.key}
                    value={item.customValues?.[column.key] ?? ""}
                    onChange={(value) =>
                      onItemChange(item.id, {
                        customValues: {
                          ...(item.customValues ?? {}),
                          [column.key]: value,
                        },
                      })
                    }
                  />
                ))}
                <div className="sticky right-0 z-10 flex items-center justify-center gap-1 bg-background pl-2">
                  <ActionButton title={`${text.moveUp}: ${item.nameEn}`} disabled={controlsBusy || itemIndex === 0} onClick={() => onMoveItem(item.id, "up")}>
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </ActionButton>
                  <ActionButton title={`${text.moveDown}: ${item.nameEn}`} disabled={controlsBusy || itemIndex === sectionItems.length - 1} onClick={() => onMoveItem(item.id, "down")}>
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </ActionButton>
                  <ActionButton title={`${text.duplicateItem}: ${item.nameEn}`} disabled={controlsBusy} onClick={() => onDuplicateItem(item.id)}>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  </ActionButton>
                  <ActionButton title={`${text.deleteItem}: ${item.nameEn}`} disabled={controlsBusy} destructive onClick={() => onDeleteItem(item.id)}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </ActionButton>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function getStatusLabel(status: ItemStatus, language: "en" | "zh"): string {
  const labels: Record<ItemStatus, { en: string; zh: string }> = {
    normal: { en: "Normal", zh: "正常" },
    included: { en: "Included", zh: "已包含" },
    in_package: { en: "In Package", zh: "打包内" },
    optional: { en: "Optional", zh: "可选" },
    tbc: { en: "TBC", zh: "待确认" },
    actual_cost: { en: "Actual Cost", zh: "实报实销" },
    hidden: { en: "Hidden", zh: "隐藏" },
  };

  return labels[status][language];
}

const en = {
  module: "Module",
  itemEn: "Item EN",
  itemZh: "Item ZH",
  unit: "Unit",
  days: "Days",
  qty: "Qty",
  rate: "Rate",
  amount: "Amount",
  status: "Status",
  client: "Client",
  tax: "Tax",
  total: "Total",
  actions: "Actions",
  addItem: "Add item",
  moveUp: "Move up",
  moveDown: "Move down",
  deleteModule: "Delete module",
  duplicateItem: "Duplicate item",
  deleteItem: "Delete item",
};

const zh: typeof en = {
  module: "模块",
  itemEn: "英文项目",
  itemZh: "中文项目",
  unit: "单位",
  days: "天数",
  qty: "数量",
  rate: "单价",
  amount: "金额",
  status: "状态",
  client: "客户可见",
  tax: "计税",
  total: "计入总价",
  actions: "操作",
  addItem: "添加项目",
  moveUp: "上移",
  moveDown: "下移",
  deleteModule: "删除模块",
  duplicateItem: "复制项目",
  deleteItem: "删除项目",
};

type CellInputProps = {
  type?: "text" | "number";
  step?: string;
  value: string | number;
  onChange: (value: string) => void;
};

function CellInput({ type = "text", step, value, onChange }: CellInputProps) {
  return (
    <input
      className={cn(
        "h-8 min-w-0 rounded-md border border-border bg-background px-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20",
        type === "number" && "text-right",
      )}
      type={type}
      step={step}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function CheckCell({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <input
      type="checkbox"
      className="mx-auto mt-2 h-4 w-4 accent-primary"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

function ActionButton({
  children,
  destructive = false,
  disabled,
  onClick,
  title,
}: {
  children: React.ReactNode;
  destructive?: boolean;
  disabled: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40",
        destructive && "text-destructive",
      )}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
